"use client";
import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Image from "next/image";
import time from "@/public/images/time.svg";
import view from "@/public/images/view.svg";
import euc from "@/public/images/ecu.svg";
import locationGrey from "@/public/images/locationGrey.svg";
import cost from "@/public/images/cost.svg";
import blackCap from "@/public/images/blackCap.svg";
import heart from "@/public/images/heart.svg";
import redHeart from "@/public/icons/redHeart.svg";
import capB from "@/public/images/capB.svg";
import capblue from "@/public/images/capblue.svg";
import book from "@/public/images/book.svg";
import fire from "@/public/images/fire.svg";
import { useTranslate } from "@/components/base/providers/TranslateProv";
import { Faculty } from "@/types/Faculty";
import { University } from "@/types/university";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import MyLink from "../myLink/MyLink";
import { formatNumber } from "@/utils/format viwes number/formatNumber";
import {
  useAddToUniversitiesFavoritesMutation,
  useRemoveToUniversitiesFavoritesMutation,
} from "@/redux/features/api/Favorites/favoritesApiSlice";
import { toast } from "react-toastify";
import { getCookie } from "cookies-next";

const FacultyCard = ({
  university,
  faculty,
  hide = false,
  onlyHideImage = false,
  specialize = false,
  openFaculty = false,
  name,
  images,
  type,
  image,
  location,
  isFavoriteProp,
  isFavoriteId,
  specialization_id,
  locationTitle,
}: {
  university?: University;
  name?: any;
  images?: any;
  type?: any;
  image?: any;
  location?: any;
  faculty: any;
  hide?: boolean;
  onlyHideImage?: boolean;
  specialize?: boolean;
  openFaculty?: boolean;
  isFavoriteProp?: boolean;
  isFavoriteId?: any;
  specialization_id?: any;
  locationTitle?: string;
}) => {
  const { locale }: { locale: "ar" | "en" } = useTranslate();
  const lang: "ar" | "en" = locale as "ar" | "en";
  // Format dates based on locale
  const formatDate = (dateString: string | undefined, locale: "ar" | "en") => {
    if (!dateString) return "0";
    const date = new Date(dateString);
    return format(date, "PPP", { locale: locale === "ar" ? ar : enUS });
  };

  const [isFavorite, setIsFavorite] = useState<boolean>(
    isFavoriteProp ? isFavoriteProp : faculty?.favorite
  );
  const [addToFavorites, {}] = useAddToUniversitiesFavoritesMutation();
  const [removeToFavorites, {}] = useRemoveToUniversitiesFavoritesMutation();
  const handleAddToFavorites = async () => {
    const token = getCookie("token");
    if (token) {
      if (faculty || specialization_id) {
        try {
          await addToFavorites(
            specialization_id
              ? { specialization_id }
              : { faculty_id: faculty.id }
          ).unwrap();
          toast.success(
            locale === "ar"
              ? "تمت الاضافة الى المفضلات بنجاح"
              : "Added to favorites successfully"
          );
          setIsFavorite(true);
        } catch (error) {
          toast.error(locale === "ar" ? "حدث خطا ما " : "An error occurred");
          console.error("Error adding to favorites:", error);
        }
      }
    } else toast.error(locale === "ar" ? "يجب تسجيل الدخول" : "Please login");
  };
  const handleRemoveToFavorites = async () => {
    if (specialization_id || faculty?.id) {
      try {
        await removeToFavorites(
          specialization_id
            ? { specialization_id }
            : { faculty_id: faculty?.id }
        ).unwrap();
        toast.success(
          locale === "ar"
            ? "تم الحذف من المفضلات بنجاح"
            : "Removed from favorites successfully"
        );
        setIsFavorite(false);
      } catch (error) {
        toast.error(locale === "ar" ? "حدث خطا ما " : "An error occurred");
        console.error("Error adding to favorites:", error);
      }
    }
  };

  return (
    <div className="w-full relative rounded-[10px] bg-white p-4 flex flex-col justify-between gap-4 border border-[#EBEBEB]">
      {/* Top Labels */}
      {/* {!onlyHideImage && ( */}
      <div
        className={`${
          onlyHideImage ? "" : images ? "absolute right-10 top-8 z-40" : ""
        } flex items-center gap-3`}
      >
        {faculty.nominate_it ? (
          <div className="bg-white py-2 px-7 rounded-full flex items-center gap-3">
            <Image src={capB} alt="Nominate" />
            <p className="text-14 text-darkText">
              {lang === "ar" ? "نرشحها" : "Recommended"}
            </p>
          </div>
        ) : (
          ""
        )}
        {faculty.trending_now ? (
          <div className="bg-white py-2 px-7 rounded-full flex items-center gap-3">
            <Image src={fire} alt="Trending" />
            <p className="text-14 text-darkText">
              {lang === "ar" ? "رائج الان" : "Trending Now"}
            </p>
          </div>
        ) : (
          ""
        )}
      </div>
      {/* )} */}
      {/* Swiper for Images */}
      {images && !onlyHideImage && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={10}
          slidesPerView={1}
          autoplay={{ delay: 3000 }}
          pagination={{
            clickable: true,
            renderBullet: (index: number, className: string) =>
              `<span class="${className} !rounded-[10px] !top-2 !bg-primaryColor !w-[30px] !h-[6px] transition"></span>`,
          }}
          dir="rtl"
          className="!pb-16 w-full h-[250px]"
        >
          {images.map((img: any, index: number) => (
            <SwiperSlide key={index}>
              <div className="relative w-full h-full">
                <Image
                  src={img.image}
                  alt={`University Image ${index + 1}`}
                  className="rounded-[10px] object-cover"
                  fill
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Info Row */}
      {!hide && (
        <div className="flex items-center w-full justify-between">
          <div className="flex items-center justify-between w-full gap-3">
            <div className="flex items-center  gap-2">
              <Image src={time} alt="Time" />
              <p className="text-12 text-nowrap text-descriptionText">
                {lang === "ar"
                  ? `منذ ${formatDate(faculty.created_at, locale)}`
                  : `Since ${formatDate(faculty.created_at, locale)}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Image src={view} alt="View" />
              <p className="text-12 text-nowrap text-descriptionText">
                {formatNumber(faculty.viewing_number)}{" "}
                {lang === "ar" ? "مشاهدة" : "Views"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Faculty Name */}
      <p className="bg-[#F8F9FA] w-full px-2 text-center flex-wrap text-18 text-primaryColor font-[600] py-2 flex items-center justify-center gap-2 rounded-[10px]">
        {specialize ? (
          <Image src={book} alt="specialize" />
        ) : (
          <Image src={capblue} alt="Faculty" />
        )}

        {faculty.name[lang]}
      </p>

      {/* University Info */}
      {!hide && (
        <div className="flex items-center gap-3">
          {image && (
            <Image
              src={image}
              alt="University Image"
              className="h-[96] object-contain"
              width={96}
              height={96}
            />
          )}
          <div>
            <p className="text-20 text-darkText font-[500]">{name[lang]}</p>
            <div className="flex items-center gap-3">
              <Image src={locationGrey} alt="Location Icon" />
              <p className="text-16 text-descriptionText">
                {location && location?.length > 0
                  ? location[0]?.title[locale]
                  : location?.title
                  ? location?.title[locale]
                  : locationTitle
                  ? locationTitle
                  : ""}
              </p>
            </div>
            {type && (
              <p className="text-[12px] mt-2.5 w-fit leading-none whitespace-nowrap text-darkText font-[500] border border-[#EBEBEB] py-2 px-4 rounded-full">
                {type[`name_${locale}`]}
              </p>
            )}
          </div>
        </div>
      )}

      {onlyHideImage &&
        hide &&
        faculty.short_description &&
        faculty.short_description[lang] && (
          <p className=" text-16 text-descriptionText line-clamp-2">
            {faculty.short_description[lang]}
          </p>
        )}

      {/* Faculty Details */}
      <div className={`grid grid-cols-${specialize ? "2" : "3"} gap-3`}>
        <div className="bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
          <Image src={time} alt="Duration" />
          <p className="text-14 text-darkText text-center">
            {lang === "ar" ? "سنوات الدراسه" : "Years of Study"}
          </p>
          <p className="text-14 text-[#ACB5BB] font-[600] text-center">
            {faculty.duration_of_study[lang]}
          </p>
        </div>
        <div className="bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
          <Image src={cost} alt="Cost" />
          <p className="text-14 text-darkText text-center">
            {lang === "ar" ? "التكلفة السنوية" : "Annual Cost"}
          </p>
          {specialize ? (
            <p className="text-14 text-[#ACB5BB] font-[600] text-center">
              {faculty.annual_expenses_in_dollar[lang]}
            </p>
          ) : (
            <p className="text-14 text-[#ACB5BB] font-[600] text-center">
              {faculty.expenses[lang]}
            </p>
          )}
        </div>
        {!specialize && (
          <div className="bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
            <Image src={blackCap} alt="Specialization" />
            <p className="text-14 text-darkText text-center">
              {lang === "ar" ? "التخصصات" : "Specializations"}
            </p>
            <p className="text-14 text-[#ACB5BB] font-[600] text-center">
              {faculty?.specialization_count || "0"}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {specialize && !openFaculty ? (
          faculty.article_id ? (
            <MyLink
              href={`/articles/${faculty.article_id}`}
              className="text-20 text-white bg-primaryColor h-[58px] grow flex justify-center items-center rounded-[10px]"
            >
              {lang === "ar" ? "عرض تفاصيل" : "View Details"}
            </MyLink>
          ) : (
            <button
              disabled
              className="text-20 text-[#FF4F4F] border border-[#FF4F4F] h-[58px] grow flex justify-center items-center rounded-[10px] cursor-not-allowed"
            >
              {lang === "ar"
                ? "جاري العمل علي مقاله مفصله"
                : "A detailed article is underway."}
            </button>
          )
        ) : (
          <MyLink
            href={`/faculities/${
              faculty?.faculty_id ? faculty.faculty_id : faculty?.id
            }`}
            className="text-20 text-white bg-primaryColor h-[58px] grow flex justify-center items-center rounded-[10px]"
          >
            {lang === "ar" ? "عرض تفاصيل" : "View Details"}
          </MyLink>
        )}

        <div className="h-[58px] rounded-[15px] w-[58px] border border-[#EBEBEB] flex items-center justify-center cursor-pointer">
          {isFavorite ? (
            <Image
              onClick={handleRemoveToFavorites}
              src={redHeart}
              alt="redHeart"
            />
          ) : (
            <Image onClick={handleAddToFavorites} src={heart} alt="heart" />
          )}{" "}
        </div>
      </div>
    </div>
  );
};

export default FacultyCard;
